import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const label = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
const short = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-AU", { month: "short" });

const OrderList = ({ order, months, onSave }) => {
  const [list, setList] = useState(order);
  const [drag, setDrag] = useState(null);
  useEffect(() => setList(order), [order]);
  const move = (from, to) => {
    if (to < 0 || to >= list.length || from === to) return;
    const next = [...list];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setList(next);
  };
  const dirty = list.map((p) => p.pack_id).join() !== order.map((p) => p.pack_id).join();
  const nextMonth = (id) => { const m = months.find((x) => x.pack_id === id); return m ? short(m.month) : ""; };
  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-wider text-[#8C827A]">Feature order: drag to rearrange. After you save, the top pack is featured this month and the rest follow in order.</p>
      <ul data-testid="feature-order-list" className="space-y-1.5">
        {list.map((p, i) => (
          <li key={p.pack_id} data-testid={`feature-order-item-${p.slug}`} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => { e.preventDefault(); if (drag !== null && drag !== i) { move(drag, i); setDrag(i); } }} onDragEnd={() => setDrag(null)}
            className={`flex items-center gap-3 border px-3 py-2 bg-[#FAF7F2] cursor-grab active:cursor-grabbing transition-colors ${drag === i ? "border-[#C59B27] bg-[#C59B27]/10" : "border-[#E2D9CC]"}`}>
            <GripVertical className="w-4 h-4 text-[#8C827A]" />
            <span className="w-6 text-xs font-mono-accent text-[#C59B27]">{i + 1}</span>
            <span className="flex-1 text-sm text-[#3B1B48]">{p.name}</span>
            {!dirty && <span data-testid={`feature-order-next-${p.slug}`} className="text-[11px] text-[#8C827A]">next: {nextMonth(p.pack_id)}</span>}
            <button data-testid={`feature-order-up-${p.slug}`} onClick={() => move(i, i - 1)} disabled={i === 0} className="p-1 text-[#3B1B48] disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
            <button data-testid={`feature-order-down-${p.slug}`} onClick={() => move(i, i + 1)} disabled={i === list.length - 1} className="p-1 text-[#3B1B48] disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
      {dirty && (
        <div className="flex gap-2">
          <Button size="sm" data-testid="feature-order-save" onClick={() => onSave(list.map((p) => p.pack_id))} className="rounded-none">Save order</Button>
          <Button size="sm" variant="ghost" data-testid="feature-order-reset" onClick={() => setList(order)} className="rounded-none">Reset</Button>
        </div>
      )}
    </div>
  );
};

export const FeaturedPacks = ({ refreshKey }) => {
  const [data, setData] = useState({ order: [], months: [] });
  const load = () => api.get("/packs-featured").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (ids) => {
    try { const { data: d } = await api.put("/packs-featured/order", { pack_ids: ids }); setData(d); toast.success(`Order saved: ${d.months[0]?.name} is featured this month`); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data.order.length) return null;
  return (
    <div data-testid="featured-packs" className="border border-[#C59B27] bg-white p-6">
      <h2 className="font-display text-2xl text-[#3B1B48] flex items-center gap-2"><Star className="w-5 h-5 text-[#C59B27]" strokeWidth={1.5} />Pack of the month</h2>
      <p className="text-sm text-[#8C827A] mt-1 mb-5">Your shop's homepage features one published pack each month, working down your order and starting again at the top. It reads <code>/api/public/featured-pack</code>.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {data.order.length > 1 ? <OrderList order={data.order} months={data.months} onSave={save} />
          : <p data-testid="feature-order-hint" className="text-sm text-[#8C827A]">Publish another pack with two or more guides to start the monthly rotation and set its order here.</p>}
        <ol className="space-y-2">
          {data.months.map((r, i) => (
            <li key={r.month} data-testid={`featured-month-${r.month}`} className={`flex justify-between gap-3 text-sm py-1.5 border-b border-[#E2D9CC] ${i === 0 ? "font-semibold text-[#3B1B48]" : "text-[#1C181D]"}`}>
              <span>{label(r.month)}{i === 0 && <span className="ml-2 text-[11px] uppercase tracking-wider text-[#2D6A4F]">now</span>}</span>
              <span className="text-right">{r.name}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
